import React, { useMemo, useState } from "react";

import { AddRegular, ArrowSortRegular, InfoRegular } from "@fluentui/react-icons";
import { Button, Dropdown, Text, Option, mergeClasses } from "@fluentui/react-components";

import { Stack } from "@/components/stack";
import { PlaybookCard } from "@/components/playbook-card";
import { PlaybookViewer } from "@/components/playbook-viewer";

import { PlaybookEditor } from "./playbook-editor";
import { useMyPlaybooksStyles } from "./styles";

import { SORT_PLAYBOOKS_OPTIONS, SortPlaybooksBy, useDocumentClassification } from "@/context/document-classification";

import { DEFAULT_CUSTOMER_RULE_ID } from "@/constants";
import { Playbook } from "@/types/risk-analysis";

interface MyPlaybooksProps extends React.ComponentPropsWithoutRef<"div"> {
  playbooks: Playbook[];
}

export function MyPlaybooks({ playbooks, className, ...props }: MyPlaybooksProps) {
  const styles = useMyPlaybooksStyles();
  const { sortPlaybooksBy, sortPlaybooks } = useDocumentClassification();

  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook>();
  const [page, setPage] = useState<"view-playbook" | "new-playbook" | "edit-playbook" | "main">("main");

  const visiblePlaybooks = useMemo(
    () =>
      playbooks.map((playbook) => ({
        ...playbook,
        rules: playbook.rules.map((rule) => ({
          ...rule,
          selectedCustomerRuleId: undefined,
          customerRules: rule.customerRules.filter((fallbackRule) => fallbackRule.id !== DEFAULT_CUSTOMER_RULE_ID),
        })),
      })),
    [playbooks]
  );

  const handleOpenPlaybook = (playbook: Playbook) => {
    setSelectedPlaybook(playbook);
    setPage("view-playbook");
  };

  const handleEditPlaybook = (playbook: Playbook) => {
    setSelectedPlaybook(playbook);
    setPage("edit-playbook");
  };

  const handleGoBack = () => {
    setSelectedPlaybook(undefined);
    setPage("main");
  };

  if (page === "new-playbook") {
    return <PlaybookEditor onGoBack={handleGoBack} />;
  }
  if (page === "edit-playbook" && selectedPlaybook) {
    return <PlaybookEditor playbook={selectedPlaybook} onGoBack={handleGoBack} />;
  }
  if (page === "view-playbook" && selectedPlaybook) {
    return <PlaybookViewer playbook={selectedPlaybook} onGoBack={handleGoBack} />;
  }
  return (
    <div
      role="tabpanel"
      aria-labelledby="My Playbooks"
      className={mergeClasses(styles.container, className)}
      {...props}
    >
      <Stack align="center" justify="between" gap="1rem">
        <Button appearance="primary" icon={<AddRegular />} onClick={() => setPage("new-playbook")}>
          New playbook
        </Button>
        <Dropdown
          placeholder="Sort by"
          aria-label="Sort playbooks"
          expandIcon={<ArrowSortRegular />}
          selectedOptions={[sortPlaybooksBy]}
          value={sortPlaybooksBy && SORT_PLAYBOOKS_OPTIONS[sortPlaybooksBy]}
          onOptionSelect={(_, data) => sortPlaybooks(data.optionValue as SortPlaybooksBy)}
        >
          {Object.entries(SORT_PLAYBOOKS_OPTIONS).map(([key, label]) => (
            <Option key={key} value={key}>
              {label}
            </Option>
          ))}
        </Dropdown>
      </Stack>
      {visiblePlaybooks.length === 0 && (
        <Stack direction="column" align="center" justify="center" style={{ marginTop: "1rem" }}>
          <InfoRegular style={{ fontSize: "1.5rem" }} />
          <Text>No playbooks found!</Text>
        </Stack>
      )}
      {visiblePlaybooks.length > 0 && (
        <Stack direction="column" gap="1rem" className={styles.playbooks}>
          {visiblePlaybooks.map((playbook) => (
            <PlaybookCard
              key={playbook.id}
              playbook={playbook}
              onOpen={() => handleOpenPlaybook(playbook)}
              onEdit={() => handleEditPlaybook(playbook)}
            />
          ))}
        </Stack>
      )}
    </div>
  );
}
